import { ledgerKindLabel } from '@/components/ledgerFilters'
import type { WarehouseLedgerTransactionItem } from '@/services/warehouseTypes'
import { normalizeBulkSku } from '@/utils/normalizeBulkSku'

/** Override-map keys stored in BookKeeper but not compared to Bright Sites snapshots. */
export const BOOKKEEPER_ONLY_OVERRIDE_FIELDS = new Set(['notes', 'store', 'unit_cost'])

export function formatLedgerFieldValue(key: string, value: unknown): string {
  if (value == null || value === '') return '—'
  if (key === 'kind' && typeof value === 'string') return ledgerKindLabel(value as never)
  if (key === 'bk_sku' && typeof value === 'string') return normalizeBulkSku(value)
  if (key === 'unit_cost' || key === 'signed_cases') {
    const num = Number(value)
    return Number.isFinite(num) ? String(num) : String(value)
  }
  return String(value)
}

function currentLedgerFieldValue(row: WarehouseLedgerTransactionItem, key: string): unknown {
  switch (key) {
    case 'txn_date':
      return row.txn_date
    case 'bk_sku':
      return row.bk_sku
    case 'kind':
      return row.kind
    case 'signed_cases':
      return row.signed_cases
    case 'store':
      return row.store
    case 'unit_cost':
      return row.unit_cost
    case 'product_name':
      return row.product_name
    case 'order_number':
      return row.order_number
    case 'username':
      return row.username
    case 'notes':
      return row.notes
    default:
      return null
  }
}

/** True when override_map has the field and current value differs from BS snapshot. */
export function fieldOverrideDiffersFromSnapshot(
  row: WarehouseLedgerTransactionItem,
  key: string
): boolean {
  if (BOOKKEEPER_ONLY_OVERRIDE_FIELDS.has(key)) return false
  if (!(key in row.override_map)) return false
  const snapshot = row.bs_upstream_snapshot ?? {}
  const upstream = snapshot[key]
  if (upstream == null) return true
  const current = currentLedgerFieldValue(row, key)
  return formatLedgerFieldValue(key, upstream) !== formatLedgerFieldValue(key, current)
}

/** True when at least one override entry still differs from the BS snapshot. */
export function rowHasMeaningfulOverrides(row: WarehouseLedgerTransactionItem): boolean {
  return Object.keys(row.override_map).some((key) => fieldOverrideDiffersFromSnapshot(row, key))
}
